import React, { useState, useRef } from "react";
import DriverBulkUploadService from "../services/DriverBulkUploadService";
import { toast } from "react-toastify";
import {
  FaUpload,
  FaDownload,
  FaTimes,
  FaCheckCircle,
  FaExclamationTriangle,
  FaSpinner,
  FaFileExcel,
  FaFileCsv,
} from "react-icons/fa";

const SimpleDriverBulkUploadModal = ({ isOpen, onClose, onSuccess }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [results, setResults] = useState(null);
  const fileInputRef = useRef(null);

  const resetModal = () => {
    setSelectedFile(null);
    setPreview(null);
    setIsUploading(false);
    setUploadProgress(0);
    setResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      resetModal();
      onClose();
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validation = DriverBulkUploadService.validateFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    setSelectedFile(file);

    try {
      const previewData = await DriverBulkUploadService.getFilePreview(file, 10);
      setPreview(previewData);
    } catch (error) {
      toast.error(`Failed to preview file: ${error.message}`);
      setSelectedFile(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setUploadProgress(0);
    setResults(null);

    try {
      const uploadResults = await DriverBulkUploadService.bulkUploadDrivers(
        selectedFile,
        setUploadProgress
      );

      setResults(uploadResults.data);
      
      if (uploadResults.success && uploadResults.data.successful > 0) {
        onSuccess?.();
      } else if (uploadResults.data && uploadResults.data.errors && uploadResults.data.errors.length > 0) {
        // Show error toast if there are errors
        const errorCount = uploadResults.data.errors.length;
        toast.error(`Upload completed with ${errorCount} error(s). Please check the details below.`);
      }
    } catch (error) {
      console.error("Quick Upload error:", error);
      
      // Extract error message from service response or error object
      let errorMessage = "Upload failed. Please try again.";
      let phoneNumber = "N/A";
      
      if (error.response) {
        const status = error.response.status;
        if (status === 404) {
          errorMessage = "Backend server not found (404). Please ensure the backend server is running.";
        } else if (status === 401) {
          errorMessage = "Authentication failed. Please log in again.";
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        } else {
          errorMessage = `Request failed with status code ${status}`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Try to extract phone number from preview if available
      if (preview && preview.rows && preview.rows.length > 0 && preview.rows[0][0]) {
        phoneNumber = preview.rows[0][0];
      }
      
      toast.error(errorMessage);
      setResults({
        successful: 0,
        failed: 1,
        totalProcessed: 1,
        errors: [{ row: 1, phone: phoneNumber, error: errorMessage }],
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadTemplate = () => {
    DriverBulkUploadService.createSampleTemplate();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Quick Upload Drivers</h2>
          {!isUploading && (
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 text-xl"
            >
              <FaTimes />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {!results ? (
            <>
              {/* File Format Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-blue-900 mb-2">File Format</h3>
                <p className="text-blue-800 text-sm mb-2">
                  Upload a CSV or Excel file with the following columns:
                </p>
                <div className="text-blue-800 text-sm">
                  <p className="font-medium">Required columns:</p>
                  <ul className="list-disc list-inside ml-2 mt-1">
                    <li>Mobile Number</li>
                    <li>Contact Source</li>
                    <li>City</li>
                  </ul>
                  <p className="mt-2 text-xs">
                    Example: <code className="bg-blue-100 px-1 rounded">9387878787 | WhatApp Group | Hyderabad</code>
                  </p>
                </div>
              </div>

              {/* Template Download */}
              <div className="mb-6">
                <button
                  onClick={handleDownloadTemplate}
                  className="text-blue-600 hover:text-blue-700 text-sm flex items-center"
                >
                  <FaDownload className="mr-2" />
                  Download CSV Template
                </button>
              </div>

              {/* File Upload */}
              {!selectedFile ? (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
                  <FaUpload className="mx-auto text-4xl text-gray-400 mb-4" />
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept=".csv,.xlsx,.xls"
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 font-medium mb-2"
                  >
                    Select CSV or Excel File
                  </button>
                  <p className="text-gray-600 text-sm">
                    Maximum file size: 10MB. CSV and Excel files (.csv, .xlsx, .xls) are supported.
                  </p>
                </div>
              ) : (
                <>
                  {/* File Info */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        {selectedFile.name.endsWith(".xlsx") || selectedFile.name.endsWith(".xls") ? (
                          <FaFileExcel className="text-green-600 text-2xl mr-3" />
                        ) : (
                          <FaFileCsv className="text-blue-600 text-2xl mr-3" />
                        )}
                        <div>
                          <p className="font-medium text-gray-900">{selectedFile.name}</p>
                          <p className="text-sm text-gray-600">
                            Size: {(selectedFile.size / 1024).toFixed(2)} KB | Rows: {preview?.totalRows || 0}
                          </p>
                        </div>
                      </div>
                      {!isUploading && (
                        <button
                          onClick={() => {
                            setSelectedFile(null);
                            setPreview(null);
                            fileInputRef.current.value = "";
                          }}
                          className="text-blue-600 hover:text-blue-700 text-sm"
                        >
                          Change File
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Preview Table */}
                  {preview && (
                    <div className="border rounded-lg overflow-hidden mb-6">
                      <div className="overflow-x-auto max-h-64">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              {preview.headers.map((header, index) => (
                                <th
                                  key={index}
                                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                >
                                  {header}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {preview.rows.map((row, rowIndex) => (
                              <tr key={rowIndex}>
                                {row.map((cell, cellIndex) => (
                                  <td
                                    key={cellIndex}
                                    className="px-4 py-2 text-sm text-gray-900"
                                  >
                                    {cell || "-"}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {preview.totalRows > preview.rows.length && (
                        <div className="bg-gray-50 px-4 py-2 text-xs text-gray-600 text-center">
                          Showing first {preview.rows.length} of {preview.totalRows} rows
                        </div>
                      )}
                    </div>
                  )}

                  {/* Upload Button */}
                  {!isUploading ? (
                    <div className="flex justify-end gap-3">
                      <button
                        onClick={handleClose}
                        className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleUpload}
                        className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
                      >
                        <FaUpload className="mr-2" />
                        Upload Drivers
                      </button>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <FaSpinner className="animate-spin mx-auto text-3xl text-blue-500 mb-2" />
                      <p className="text-gray-600">Uploading drivers...</p>
                      <div className="mt-4 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                      <p className="text-sm text-gray-600 mt-2">{uploadProgress}% Complete</p>
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            /* Results */
            <div>
              <div className="text-center mb-6">
                {results.successful > 0 ? (
                  <FaCheckCircle className="mx-auto text-6xl text-green-500 mb-4" />
                ) : (
                  <FaExclamationTriangle className="mx-auto text-6xl text-red-500 mb-4" />
                )}
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Upload Complete</h3>
              </div>

              {/* Results Summary */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {results.totalProcessed || 0}
                  </div>
                  <div className="text-sm text-blue-700">Total Processed</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {results.successful || 0}
                  </div>
                  <div className="text-sm text-green-700">Successful</div>
                </div>
                <div className="bg-red-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-red-600">
                    {results.failed || 0}
                  </div>
                  <div className="text-sm text-red-700">Failed</div>
                </div>
              </div>

              {/* Error Details */}
              {results.errors && results.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <h4 className="font-semibold text-red-900 mb-3">
                    Failed Records ({results.errors.length})
                  </h4>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {results.errors.map((error, index) => (
                      <div
                        key={index}
                        className="text-sm text-red-800 bg-red-100 rounded p-2"
                      >
                        <strong>Row {error.row}:</strong> {error.phone} - {error.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    resetModal();
                    onClose();
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
                {results.successful > 0 && (
                  <button
                    onClick={() => {
                      resetModal();
                      onClose();
                      onSuccess?.();
                    }}
                    className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    Done
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SimpleDriverBulkUploadModal;
